import { useState } from "react";
import { useLang } from "@/i18n/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { track } from "@/lib/analytics";

const Schema = z.object({
  name: z.string().trim().min(1).max(120),
  company: z.string().trim().max(160).optional(),
  email: z.string().trim().email().max(200),
  phone: z.string().trim().max(40).optional(),
  message: z.string().trim().min(1).max(2000),
});

const ContactForm = () => {
  const { t, lang } = useLang();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", message: "" });

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = Schema.safeParse(form);
    if (!parsed.success) {
      toast.error(t.contact.error);
      return;
    }
    setLoading(true);
    try {
      track("contact_submit", "contact_page");
      const { error } = await supabase.functions.invoke("send-contact", {
        body: { ...parsed.data, type: "contact", lang },
      });
      if (error) throw error;
      toast.success(t.contact.success);
      setForm({ name: "", company: "", email: "", phone: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error(t.contact.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="c-name">{t.demo.name}</Label>
          <Input id="c-name" required maxLength={120} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t.demo.namePh} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="c-company">{t.demo.company}</Label>
          <Input id="c-company" maxLength={160} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder={t.demo.companyPh} />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="c-email">{t.demo.email}</Label>
          <Input id="c-email" type="email" required maxLength={200} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t.demo.emailPh} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="c-phone">{t.demo.phone}</Label>
          <Input id="c-phone" maxLength={40} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={t.demo.phonePh} />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="c-msg">{t.demo.message}</Label>
        <Textarea id="c-msg" required rows={5} maxLength={2000} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder={t.demo.messagePh} />
      </div>
      <Button type="submit" size="lg" disabled={loading} className="w-full rounded-full h-12 text-base">
        {loading ? t.contact.sending : t.contact.submit}
      </Button>
    </form>
  );
};

export default ContactForm;
